export type GeoLocation = {
  latitude: number
  longitude: number
}

export type Property = {
  id: string
  baths: number | null
  beds: number | null
  cars: number | null
  fullAddress: string
  geohash_8: string
  hasActiveBiddles: boolean
  landSize: number
  location: GeoLocation
  postCode: string
  propertyType: string
  state: string
  streetName: string
  streetNumber: string
  streetType: string
  suburb: string
  unitNumber: string
  createdAt: Date | null
}

export type Biddle = {
  id: string
  propertyID: string
  price: number
  settlement: string
  deposit: string
  notes: string
  expiry: Date | null
  isOpenOffer: boolean
  timeCreated: Date
  isDeleted: boolean
  ownerHasViewed: boolean
  ownerHasAccepted: boolean
  buyerBiddleTag: string
  ownerBiddleTag: string | null
}

export type UserProfile = {
  id: string
  email: string
  displayName: string | null
  photoURL: string | null
  favProperties: string[]
  sentBiddles: string[]
  receivedBiddles: string[]
  letterCredits: number
  isIdentityVerified: boolean
  verificationMethod: string | null
  emailVerified: boolean
}

export type LetterPack = {
  sku: string
  priceCents: number
  letterCount: number
}

export type LetterConfig = {
  enabled: boolean
  packs: LetterPack[]
  templateVersion: string
  whitelistUids: string[]
  upsell: {
    headline: string
    body: string
  }
}

export type MemberCard = {
  id: string
  active: boolean
  campaignStart: Date
  campaignEnd: Date
  postcodes: string[]
  imageUrl: string
  agentName?: string
  agentCompany?: string
  headline?: string
  phone?: string
  email?: string
  websiteUrl: string
}

export type PropertyCollection = {
  id: string
  name: string
  nameLower: string
  description: string
  isFavourites: boolean
  itemCount: number
  updatedAt: Date
  createdAt: Date
}

export type CollectionItem = {
  propertyId: string
  fullAddress: string
  note: string
  addedAt: Date
  updatedAt: Date
}

/** Create-wizard steps that can be persisted as a draft (past agreement). */
export type BiddleDraftStep = 'price' | 'terms' | 'timing' | 'extras' | 'review'

export type BiddleDraft = {
  propertyId: string
  fullAddress: string
  step: BiddleDraftStep
  agreed: boolean
  price: string
  settlement: string
  deposit: string
  flexibleSettlement: boolean
  flexibleDeposit: boolean
  isOpenOffer: boolean
  expiry: string
  notes: string
  sendLetter: boolean
  updatedAt: Date
  createdAt: Date
}

export type BiddleStatus = 'Pending' | 'Viewed' | 'In chat'

export function biddleStatus(b: Biddle): BiddleStatus {
  if (b.ownerHasAccepted) return 'In chat'
  if (b.ownerHasViewed) return 'Viewed'
  return 'Pending'
}

export function isBiddleActive(b: Biddle): boolean {
  if (b.isDeleted) return false
  if (b.isOpenOffer) return true
  if (!b.expiry) return false
  return b.expiry > new Date()
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value)
  }
  return null
}

function asLocation(value: unknown): GeoLocation {
  if (value && typeof value === 'object') {
    const v = value as Record<string, unknown>
    const latitude = asNumber(v.latitude ?? v._latitude) ?? 0
    const longitude = asNumber(v.longitude ?? v._longitude) ?? 0
    return { latitude, longitude }
  }
  return { latitude: 0, longitude: 0 }
}

function asDate(value: unknown): Date | null {
  if (!value) return null
  if (value instanceof Date) return value
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const maybe = value as { toDate: () => Date }
    return maybe.toDate()
  }
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>
    const seconds = asNumber(v.seconds ?? v._seconds)
    if (seconds != null) return new Date(seconds * 1000)
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  return null
}

export function parseProperty(id: string, data: Record<string, unknown>): Property {
  return {
    id,
    baths: asNumber(data.baths),
    beds: asNumber(data.beds),
    cars: asNumber(data.cars),
    fullAddress: String(data.fullAddress ?? ''),
    geohash_8: String(data.geohash_8 ?? ''),
    hasActiveBiddles: Boolean(data.hasActiveBiddles),
    landSize: asNumber(data.landSize) ?? 0,
    location: asLocation(data.location),
    postCode: String(data.postCode ?? ''),
    propertyType: String(data.propertyType ?? ''),
    state: String(data.state ?? ''),
    streetName: String(data.streetName ?? ''),
    streetNumber: String(data.streetNumber ?? ''),
    streetType: String(data.streetType ?? ''),
    suburb: String(data.suburb ?? ''),
    unitNumber: String(data.unitNumber ?? ''),
    createdAt: asDate(data.createdAt),
  }
}

export function parseBiddle(id: string, data: Record<string, unknown>): Biddle {
  return {
    id,
    propertyID: String(data.propertyID ?? ''),
    price: asNumber(data.price) ?? 0,
    settlement: String(data.settlement ?? ''),
    deposit: String(data.deposit ?? ''),
    notes: String(data.notes ?? ''),
    expiry: asDate(data.expiry),
    isOpenOffer: Boolean(data.isOpenOffer),
    timeCreated: asDate(data.timeCreated) ?? new Date(),
    isDeleted: Boolean(data.isDeleted),
    ownerHasViewed: Boolean(data.ownerHasViewed),
    ownerHasAccepted: Boolean(data.ownerHasAccepted),
    buyerBiddleTag: String(data.buyerBiddleTag ?? ''),
    ownerBiddleTag: data.ownerBiddleTag != null ? String(data.ownerBiddleTag) : null,
  }
}
